import React, { useMemo, useState } from 'react';
import { Share } from '@velocity-exchange/icons';
import {
	BASE_PRECISION_EXP,
	BigNum,
	MarketStatus,
	PRICE_PRECISION_EXP,
	PerpMarketConfig,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	UIOrderType,
	UIMarket,
	calculatePnlPctFromPosition,
} from '@velocity-exchange/common';
import {
	getMarginUsedForPosition,
	baseSizeDisplayForPerpMarket,
} from 'src/utils/position';
import useShowAccountValues from '../../hooks/useShowAccountValues';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';
import TableActionButton from '../Buttons/TableActionButton';
import TableV2 from '../Tables/TableV2';
import MarketCellV2 from '../Tables/cells/MarketCellV2';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import ValueChangeBlink from '../Utils/ValueChangeBlink';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import { AccountOpenPosition } from 'src/@types/positions';
import { usePerpMarketsLookup } from 'src/hooks/perpMarkets/usePerpMarketsLookup';
import useMarkPrice from 'src/hooks/useMarkPrice';
import { TakeProfitStopLossButton } from '../Buttons/TakeProfitStopLossButton';
import NumberDisplay from '../NumberDisplay/NumberDisplay';
import useClosePositionWithMarketOrder from 'src/hooks/positions/useClosePositionWithMarketOrder';
import { PRICE_FALLBACK_DISPLAY_VALUE } from 'src/components/TradeForm/consts';
import { cj } from 'src/utils/classNames';
import TextButton from 'src/components/primitives/TextButton/TextButton';
import Divider from 'src/components/primitives/Divider/Divider';
import useModal from 'src/components/Modals/hooks/useModal';
import { signDependentValue } from 'src/utils/signDependentValue';
import OpenPositionsTableMarginDisplay from 'src/components/PositionsTable/OpenPositionsTableMarginDisplay';
import useSettlePnls from 'src/hooks/drift-actions/useSettlePnls';
import IconButton from 'src/components/primitives/IconButton/IconButton';

interface OpenPositionsDesktopRowProps {
	position: AccountOpenPosition;
	tableGrid: string;
	handleSelection: (market: PerpMarketConfig) => void;
	handleShowPnlCard: () => void;
	handleShowPositionDrawer: () => void;
}

export const OpenPositionsDesktopRow = ({
	position,
	tableGrid,
	handleSelection,
	handleShowPnlCard,
	handleShowPositionDrawer,
}: OpenPositionsDesktopRowProps) => {
	const { marketSymbol, marketIndex, entryPrice, liqPrice, baseSize } =
		position;

	const marketSymbolDisplay = marketSymbol;

	const [settlingMarketIndex, setSettlingMarketIndex] = useState<
		number | undefined
	>();

	const perpMarketsLookup = usePerpMarketsLookup();
	const { show } = useModal();
	const { settlePnls } = useSettlePnls();

	const baseSizeBigNum = BigNum.from(baseSize.abs(), BASE_PRECISION_EXP);

	const uiMarket = UIMarket.createPerpMarket(marketIndex);
	const marketId = uiMarket.marketId;

	const isMarketInSettlementMode = ENUM_UTILS.match(
		position.marketAccount.status,
		MarketStatus.SETTLEMENT
	);

	const markPrice = useMarkPrice(marketId);

	const notionalSize = BigNum.from(
		position.notional,
		QUOTE_PRECISION_EXP
	).toNotional();

	const showAccountValues = useShowAccountValues();
	const [pnlReference] = useSyncLocalStorage<'mark' | 'oracle'>(
		'pnlReference',
		'mark'
	);

	const positionPnl = BigNum.from(
		pnlReference === 'mark' ? position.pnlVsMark : position.pnlVsOracle,
		QUOTE_PRECISION_EXP
	);

	const fundingPnl = BigNum.from(
		position.feesAndFundingPnl,
		QUOTE_PRECISION_EXP
	);

	const netPnl = positionPnl; // we will remove funding pnl from the UI position table pnl for now, since the UI's wording needs to improve to better inform users that pnl includes funding pnl
	const pnl = netPnl.toNum();
	const notionalPnl = showAccountValues ? `${netPnl.toNotional()}` : '∗∗∗∗∗∗';

	const driftClient = useDriftStore((s) => s.driftClient.client);
	const user = driftClient?.hasUser() ? driftClient?.getUser() : undefined;

	const percentageNum = calculatePnlPctFromPosition(
		netPnl.val,
		position,
		user
			? getMarginUsedForPosition(user, position.marketIndex, false)
			: undefined
	);

	const percentagePnl = `${percentageNum.toFixed(2)}%`;

	const priceChangePct = useMemo(() => {
		const entryNum = BigNum.from(entryPrice, PRICE_PRECISION_EXP).toNum();

		if (position.isDustPosition || entryNum <= 0) return '-';

		const markNum = markPrice.toNum();
		const percentNum = ((markNum - entryNum) / entryNum) * 100;

		return `(${percentNum.toFixed(2)}%)`;
	}, [entryPrice, position.isDustPosition, markPrice]);

	const liqPriceBigNum = BigNum.from(liqPrice, PRICE_PRECISION_EXP);

	const handleClickMarket = (e: React.MouseEvent<HTMLDivElement>) => {
		// Prevent click from also opening up position details side drawer.
		e.stopPropagation();

		handleSelection(perpMarketsLookup[marketIndex]);
	};

	const { closePositionWithMarketOrder, isPending: isClosingPosition } =
		useClosePositionWithMarketOrder({
			marketIndex,
			baseSize: BigNum.from(baseSize.abs(), BASE_PRECISION_EXP),
		});

	const handleClosePosition = (
		e: React.MouseEvent<HTMLButtonElement>,
		uiOrderType: UIOrderType
	) => {
		// Prevent click from also opening up position details side drawer.
		e.stopPropagation();

		if (uiOrderType === 'market') {
			closePositionWithMarketOrder();
			return;
		}

		show({
			type: 'close_position',
			props: { marketIndex, uiOrderType },
		});
	};

	const handleOpenTpSlOrderFormModal = () => {
		show({
			type: 'tp_sl',
			props: { marketIndex },
		});
	};

	return (
		<>
			<TableV2.BodyRow
				grid={tableGrid}
				onClick={handleShowPositionDrawer}
				className="h-13 cursor-pointer border-0"
			>
				{/** Desktop - Market + Direction */}
				<MarketCellV2
					marketSymbol={marketSymbolDisplay}
					className="w-auto py-0 whitespace-nowrap"
					onClick={handleClickMarket}
					direction={position.direction}
					marketType={uiMarket.marketType}
					marketIndex={uiMarket.marketIndex}
					isIsolated={position.isIsolated}
				/>

				{/** Size */}
				<TableV2.BodyCell className="py-0 text-text-default">
					<div className="flex flex-col gap-y-xs">
						<span
							className={cj(
								'text-left',
								position.direction === 'long'
									? 'text-text-positive-green-button'
									: 'text-text-negative-red-button'
							)}
						>
							{baseSizeDisplayForPerpMarket(
								baseSizeBigNum,
								position.marketSymbol,
								!showAccountValues
							)}
						</span>
						<ValueChangeBlink
							value={showAccountValues ? notionalSize : '$ ∗∗∗∗∗∗'}
							className="text-left"
						>
							<span className="typo-body-xs text-text-secondary">
								{showAccountValues ? notionalSize : '$ ∗∗∗∗∗∗'}
							</span>
						</ValueChangeBlink>
					</div>
				</TableV2.BodyCell>

				{/** Entry */}
				<TableV2.BodyCell className="py-0 text-text-default">
					<div className="flex flex-col text-left">
						<NumberDisplay
							value={BigNum.from(entryPrice, PRICE_PRECISION_EXP)}
							decimals={getMarketPriceDecimals(
								uiMarket.uiSymbols.baseAssetSymbol
							)}
							type="currency"
						/>
					</div>
				</TableV2.BodyCell>
				{/** Desktop - Mark (%) */}
				<TableV2.BodyCell className="py-0 text-text-default">
					<div className="flex flex-col gap-y-xs text-left">
						<NumberDisplay
							value={markPrice}
							decimals={getMarketPriceDecimals(
								uiMarket.uiSymbols.baseAssetSymbol
							)}
							type="currency"
						/>
						<span className="text-left typo-body-xs text-text-secondary">
							{priceChangePct}
						</span>
					</div>
				</TableV2.BodyCell>

				{/** PnL */}
				<TableV2.BodyCell
					className={cj(
						'py-0',
						signDependentValue(pnl, {
							positive: 'text-text-positive-green-button',
							negative: 'text-text-negative-red-button',
							zero: 'text-text-default',
						})
					)}
				>
					<span className="relative flex w-full items-center gap-x-xs">
						<Tooltip
							decoration="cursor-help"
							contentClassName="flex flex-col gap-y-sm"
							content={
								<>
									<div>
										Net P&L based on current{' '}
										{pnlReference === 'mark' ? 'mark' : 'oracle'} price. P&L
										upon closing your position may differ due to exit price.
									</div>
									<Divider className="bg-text-tertiary" />
									<div className="flex w-full flex-col gap-y-2xs">
										<div className="flex w-full items-center justify-between">
											<span>Position P&L: </span>
											<span>
												{showAccountValues
													? positionPnl.toNotional()
													: '$ ∗∗∗∗∗∗'}
											</span>
										</div>
										<div className="flex w-full items-center justify-between">
											<span>Fees and Funding: </span>
											<span>
												{showAccountValues
													? fundingPnl.toNotional()
													: '$ ∗∗∗∗∗∗'}
											</span>
										</div>
									</div>
								</>
							}
						>
							<ValueChangeBlink
								className="flex flex-col gap-y-xs"
								value={notionalPnl}
							>
								<span>{notionalPnl}</span>
								{!position.isDustPosition && (
									<span className="typo-body-xs">{percentagePnl}</span>
								)}
							</ValueChangeBlink>
						</Tooltip>

						<IconButton
							size="md"
							variant="tertiary"
							className="-m-sm p-sm"
							onClick={(e) => {
								// Prevent click from also opening up position details side drawer.
								e.stopPropagation();

								handleShowPnlCard();
							}}
							icon={Share}
						/>
					</span>
				</TableV2.BodyCell>

				{/** Margin */}
				<TableV2.BodyCell className="py-0 text-text-default">
					<OpenPositionsTableMarginDisplay marketIndex={position.marketIndex} />
				</TableV2.BodyCell>

				{/** Liq. Price */}
				<TableV2.BodyCell className="py-0 text-text-default">
					<ValueChangeBlink
						value={
							showAccountValues ? liqPriceBigNum.toNotional(true) : '$ ∗∗∗∗∗∗'
						}
					>
						{showAccountValues ? (
							liqPriceBigNum.lteZero() ? (
								PRICE_FALLBACK_DISPLAY_VALUE
							) : (
								<NumberDisplay
									value={liqPriceBigNum}
									decimals={getMarketPriceDecimals(
										uiMarket.uiSymbols.baseAssetSymbol
									)}
									type="currency"
								/>
							)
						) : (
							'$ ∗∗∗∗∗∗'
						)}
					</ValueChangeBlink>
				</TableV2.BodyCell>

				{/** TP/SL */}
				<TableV2.BodyCell className="py-0">
					<TakeProfitStopLossButton
						position={position}
						handleOpenTpSlModal={handleOpenTpSlOrderFormModal}
					/>
				</TableV2.BodyCell>

				{/** Action buttons */}
				<TableV2.BodyCell alignRight className="py-0">
					<div className="inline-flex items-center gap-2">
						{isMarketInSettlementMode && (
							<TableActionButton
								className="justify-start px-0"
								label="Settle"
								handleClick={async () => {
									setSettlingMarketIndex(position.marketIndex);
									await settlePnls(
										`position-row-settle-position-${
											position.marketIndex
										}-${Date.now()}`,
										position.marketIndex
									);
									setSettlingMarketIndex(undefined);
								}}
								isLoading={settlingMarketIndex === position.marketIndex}
							/>
						)}
						{!isMarketInSettlementMode && (
							<div className="flex items-center gap-x-md">
								<TextButton
									variant="tertiary"
									size="sm"
									onClick={(e) => handleClosePosition(e, 'market')}
									disabled={isClosingPosition}
								>
									Market
								</TextButton>
								<Divider vertical />
								<TextButton
									variant="tertiary"
									size="sm"
									onClick={(e) => handleClosePosition(e, 'limit')}
									disabled={isClosingPosition}
								>
									Limit
								</TextButton>
							</div>
						)}
					</div>
				</TableV2.BodyCell>
			</TableV2.BodyRow>
		</>
	);
};
